import React from 'react';
import {
  Caption,
  Flex,
  MailIcon,
  PhoneIcon,
  Stack,
  TinyTitleBold,
  Link as LinkComponent,
  OpenInNewIcon,
  UserIcon
} from '@kvdbil/components';
import styled from 'styled-components';
import { ContactPersonCardRecord } from '~/config/generated/graphql';
import { Image } from 'react-datocms';
import Link from '~/App/shared/components/Link';
import { useSelector } from 'react-redux';
import { localizationSelector } from '~/App/shared/selectors/localization';
import {
  formatPhoneNumberForLink,
  formatPhoneNumber
} from '~/helpers/phoneNumber';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background.light};
  border: 2px solid ${({ theme }) => theme.colors.background.gray};
  border-radius: 8px;
  overflow: hidden;
  height: fit-content;
`;

const Header = styled.div`
  background: ${({ theme }) => theme.colors.background.lightGray};
  padding: 1rem;
  display: flex;
  gap: 1rem;
`;

const List = styled.div`
  padding: 1rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.5rem;
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.background.gray};
`;

const AvatarPlaceholder = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.grayscaleToned.dark4};
  background: ${({ theme }) => theme.colors.grayscaleToned.light4};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

type Props = {
  item: ContactPersonCardRecord;
};

const ContactPersonCard = ({ item }: Props) => {
  const { domainLocale } = useSelector(localizationSelector);
  const { name, role, image, phone, email, extraDescriptiveLine, profilePage } =
    item;
  const formattedPhone = formatPhoneNumberForLink(phone ?? '');
  const displayPhone =
    domainLocale !== 'sv' ? formatPhoneNumber(formattedPhone) : phone;

  return (
    <Card>
      <Header>
        {image?.responsiveImage ? (
          <div>
            <Image
              data={image.responsiveImage}
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%'
              }}
            />
          </div>
        ) : (
          <AvatarPlaceholder>
            <UserIcon width={32} height={32} />
          </AvatarPlaceholder>
        )}

        <Stack spacing={0} style={{ width: '100%' }}>
          <Flex justify="space-between">
            {name && <TinyTitleBold as="span">{name}</TinyTitleBold>}
            {profilePage?.slug && (
              <LinkComponent path={`/${profilePage.slug}`} target="_blank">
                <OpenInNewIcon width={24} height={24} />
              </LinkComponent>
            )}
          </Flex>

          {role && <Caption>{role}</Caption>}
          {extraDescriptiveLine && <Caption>{extraDescriptiveLine}</Caption>}
        </Stack>
      </Header>
      <List>
        {phone && (
          <Row>
            <RowContent>
              <PhoneIcon width={24} height={24} />
              <Link to={`tel:${formattedPhone}`} withUnderline>
                {displayPhone}
              </Link>
            </RowContent>
            <Divider />
          </Row>
        )}
        {email && (
          <Row>
            <RowContent>
              <MailIcon width={24} height={24} />
              <Link to={`mailto:${email}`} withUnderline>
                {email}
              </Link>
            </RowContent>
            <Divider />
          </Row>
        )}
      </List>
    </Card>
  );
};

export default ContactPersonCard;
